import React, { useEffect, useState } from "react";
import { Edit2, Trash2 } from "lucide-react";
import { toast } from "react-toastify";
import DocumentForm from "./DocumentForm";
import {
  createDocument,
  deleteDocument,
  getDocuments,
} from "../../services/DocumentService";
import { Tooltip } from "@mui/material";
import DeleteConfirmModal from "../common/DeleteConfirmModal";

const TYPE_LABELS = {
  tsa_mock: "Đề thi thử TSA",
  thpt_mock: "Đề thi thử THPT",
  tsa_docs: "Tài liệu TSA",
  thpt_docs: "Tài liệu THPT",
};

export default function Documents() {
  const [formData, setFormData] = useState({
    title: "",
    url: "",
    type: "",
    description: "",
  });

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const limit = 6;
  const [isEditing, setIsEditing] = useState(null);
  const [isSearch, setIsSearch] = useState(false);
  const [listDocuments, setListDocuments] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState("");
  const [deleteModal, setDeleteModal] = useState({
    isOpen: false,
    documentId: null,
    documentTitle: "",
  });

  const handleFetch = async () => {
    try {
      const response = await getDocuments(
        currentPage,
        limit,
        searchQuery,
        typeFilter
      );
      setListDocuments(response?.data || []);
      setTotalPages(response?.totalPages || 1);
      setCurrentPage(response?.currentPage || 1);
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(message || "Lỗi khi tải danh sách tài liệu");
    }
  };

  useEffect(() => {
    handleFetch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPage, typeFilter]);

  const handleEditDocument = (document) => {
    setIsEditing(true);
    setFormData({
      _id: document._id,
      title: document.title || "",
      url: document.url || "",
      type: document.type || "",
      description: document.description || "",
    });
    // Scroll to form
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const handleDeleteDocument = (id, title) => {
    setDeleteModal({
      isOpen: true,
      documentId: id,
      documentTitle: title || "tài liệu này",
    });
  };

  const confirmDeleteDocument = async () => {
    try {
      const res = await deleteDocument(deleteModal.documentId);
      toast.success(res?.message || "Xóa tài liệu thành công");
      setListDocuments(
        listDocuments.filter((doc) => doc?._id !== deleteModal.documentId)
      );
      setDeleteModal({ isOpen: false, documentId: null, documentTitle: "" });
      handleFetch();
    } catch (error) {
      const message = error?.response?.data?.message;
      toast.error(message || "Lỗi khi xóa tài liệu");
      setDeleteModal({ isOpen: false, documentId: null, documentTitle: "" });
    }
  };

  const handleInsertDocument = async () => {
    if (validateForm()) {
      try {
        const res = await createDocument(formData);
        if (res && res.data) {
          setListDocuments([res.data, ...listDocuments]);
          handleFetch();
          toast.success(res?.message || "Tạo tài liệu thành công");
          setFormData({
            title: "",
            url: "",
            type: "",
            description: "",
          });
          setIsEditing(null);
          // Scroll to form
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      } catch (error) {
        const message = error?.response?.data?.message;
        toast.error(message || "Lỗi khi tạo tài liệu");
      }
    }
  };

  const handleUpdateDocument = async () => {
    if (validateForm()) {
      try {
        const res = await createDocument(formData);
        if (res && res.data) {
          setListDocuments(
            listDocuments.map((e) => (e._id === res.data?._id ? res.data : e))
          );
          handleFetch();
          toast.success(res?.message || "Cập nhật tài liệu thành công");
          setFormData({
            title: "",
            url: "",
            type: "",
            description: "",
          });
          setIsEditing(null);
          // Scroll to form
          window.scrollTo({ top: 0, behavior: "smooth" });
        }
      } catch (error) {
        const message = error?.response?.data?.message;
        toast.error(message || "Lỗi khi cập nhật tài liệu");
      }
    }
  };

  const validateForm = () => {
    if (!formData.title) {
      toast.error("Vui lòng nhập tiêu đề");
      return false;
    }

    if (!formData.url) {
      toast.error("Vui lòng nhập URL");
      return false;
    }

    if (!formData.type) {
      toast.error("Vui lòng chọn loại tài liệu");
      return false;
    }

    // Validate URL format
    try {
      new URL(formData.url);
    } catch (e) {
      toast.error("URL không hợp lệ");
      return false;
    }

    return true;
  };

  const handleChangeInputDocument = (event) => {
    const { name, value } = event.target;
    setFormData({
      ...formData,
      [name]: value,
    });
  };

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
  };

  const handleSearch = () => {
    if (searchQuery) {
      setIsSearch(true);
    } else {
      setIsSearch(false);
    }
    setCurrentPage(1);
    handleFetch();
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      if (searchQuery) {
        setIsSearch(true);
      } else {
        setIsSearch(false);
      }
      handleSearch();
    }
  };

  const handleTypeFilterChange = (e) => {
    setTypeFilter(e.target.value);
    setCurrentPage(1);
  };

  return (
    <div className="p-[30px] overflow-auto">
      <h2 className="text-3xl font-bold mb-6 text-gray-800">
        Quản Lý Tài Liệu
      </h2>

      {/* Document Form */}
      <DocumentForm
        isEditing={!!isEditing}
        formData={formData}
        handleInsertDocument={handleInsertDocument}
        handleChangeInputDocument={handleChangeInputDocument}
        handleUpdateDocument={handleUpdateDocument}
      />

      {/* Search and Filter Section */}
      <div className="mb-4 flex items-center space-x-2 flex-wrap gap-4">
        <input
          type="text"
          placeholder="Tìm kiếm tài liệu..."
          value={searchQuery}
          onChange={handleSearchChange}
          onKeyPress={handleKeyPress}
          className="w-96 px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring"
        />
        <button
          onClick={handleSearch}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition"
        >
          Search
        </button>
        <select
          value={typeFilter}
          onChange={handleTypeFilterChange}
          className="px-4 py-2 border rounded-md shadow-sm focus:outline-none focus:ring"
        >
          <option value="">Tất cả loại</option>
          {Object.entries(TYPE_LABELS).map(([value, label]) => (
            <option key={value} value={value}>
              {label}
            </option>
          ))}
        </select>
      </div>

      {/* Document Table */}
      <div className="bg-white shadow-md rounded-lg overflow-hidden">
        <div className="max-h-96 overflow-y-auto">
          <table className="w-full">
            <thead className="bg-gray-100 border-b">
              <tr>
                <th className="p-3 text-left">No</th>
                <th className="p-3 text-left">Tiêu đề</th>
                <th className="p-3 text-left">URL</th>
                <th className="p-3 text-left">Loại</th>
                <th className="p-3 text-left">Số lượt tải</th>
                <th className="p-3 text-left">Người tạo</th>
                {/* <th className="p-3 text-left">Ngày tạo</th> */}
                <th className="p-3 text-center">Actions</th>
              </tr>
            </thead>
            <tbody>
              {listDocuments.length > 0 ? (
                listDocuments.map((document, index) => (
                  <tr
                    key={document?._id}
                    className="border-b hover:bg-gray-50 transition"
                  >
                    <Tooltip title={document?._id} placement="top">
                      <td className="p-3">
                        {document?._id?.slice(0, 5)}...
                        {document?._id?.slice(-5)}
                      </td>
                    </Tooltip>
                    <td className="p-3">
                      <div
                        className="max-w-xs truncate"
                        title={document?.title}
                      >
                        {document?.title}
                      </div>
                    </td>
                    <td className="p-3">
                      <a
                        href={document?.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 underline truncate max-w-xs block"
                        title={document?.url}
                      >
                        {document?.url}
                      </a>
                    </td>
                    <td className="p-3">
                      <span className="px-2 py-1 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                        {TYPE_LABELS[document?.type] || document?.type}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-gray-700">
                        {document?.numberOfDơwnload || 0}
                      </span>
                    </td>
                    <td className="p-3">
                      <span className="text-sm text-gray-700">
                        {document?.createdBy?.fullName || "N/A"}
                      </span>
                    </td>
                    {/* <td className="p-3">
                      {document?.createdAt
                        ? new Date(document.createdAt).toLocaleDateString(
                            "vi-VN",
                            configDate
                          )
                        : "N/A"}
                    </td> */}
                    <td className="p-3">
                      <div className="flex items-center justify-center space-x-2 h-full min-h-[40px]">
                        <button
                          onClick={() => handleEditDocument(document)}
                          className="text-blue-500 hover:text-blue-700 transition"
                          title="Edit Document"
                        >
                          <Edit2 className="w-5 h-5" />
                        </button>
                        <button
                          onClick={() =>
                            handleDeleteDocument(document?._id, document?.title)
                          }
                          className="text-red-500 hover:text-red-700 transition"
                          title="Delete Document"
                        >
                          <Trash2 className="w-5 h-5" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="8" className="p-8 text-center text-gray-500">
                    Không có tài liệu nào
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="flex justify-between p-4 items-center">
          <span>
            Page {isSearch ? "1 / 1" : `${currentPage} / ${totalPages}`}
          </span>
          <div className="flex gap-2">
            <button
              onClick={() => setCurrentPage((p) => Math.max(p - 1, 1))}
              disabled={currentPage === 1 || isSearch}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              Previous
            </button>
            <button
              onClick={() =>
                setCurrentPage((p) => (p < totalPages ? p + 1 : p))
              }
              disabled={currentPage === totalPages || isSearch}
              className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
            >
              Next
            </button>
          </div>
        </div>
      </div>

      {/* Delete Confirm Modal */}
      <DeleteConfirmModal
        isOpen={deleteModal.isOpen}
        onClose={() =>
          setDeleteModal({ isOpen: false, documentId: null, documentTitle: "" })
        }
        onConfirm={confirmDeleteDocument}
        title="Xác nhận xóa tài liệu"
        message="Bạn có chắc chắn muốn xóa tài liệu này?"
        itemName={deleteModal.documentTitle}
      />
    </div>
  );
}
